import { useEffect, useState } from 'react';

import type { Language } from '@/shared/i18n';

import {
  fetchBloggerArticle,
  fetchBloggerArticles,
  localizeBloggerArticle,
  type BloggerPublicArticle
} from './blogApi';
import type { LocalizedBlogPost } from './blogPosts';

type BlogPostsState = {
  isLoading: boolean;
  posts: LocalizedBlogPost[];
};

type BlogPostState = {
  error: Error | null;
  isLoading: boolean;
  isNotFound: boolean;
  post: LocalizedBlogPost | null;
};

const articleListCache = new Map<Language, BloggerPublicArticle[]>();
const articleListRequests = new Map<
  Language,
  Promise<BloggerPublicArticle[]>
>();
const articleDetailCache = new Map<string, BloggerPublicArticle | null>();
const articleDetailRequests = new Map<
  string,
  Promise<BloggerPublicArticle | null>
>();

const loadArticleList = (language: Language) => {
  const cachedArticles = articleListCache.get(language);

  if (cachedArticles) {
    return Promise.resolve(cachedArticles);
  }

  const activeRequest = articleListRequests.get(language);

  if (activeRequest) {
    return activeRequest;
  }

  const request = fetchBloggerArticles(language)
    .then(articles => {
      articleListCache.set(language, articles);
      return articles;
    })
    .finally(() => {
      articleListRequests.delete(language);
    });

  articleListRequests.set(language, request);

  return request;
};

const loadArticleDetail = (slug: string, language: Language) => {
  const cacheKey = `${language}:${slug}`;

  if (articleDetailCache.has(cacheKey)) {
    return Promise.resolve(articleDetailCache.get(cacheKey) ?? null);
  }

  const activeRequest = articleDetailRequests.get(cacheKey);

  if (activeRequest) {
    return activeRequest;
  }

  const request = fetchBloggerArticle(slug, language)
    .then(article => {
      articleDetailCache.set(cacheKey, article);
      return article;
    })
    .finally(() => {
      articleDetailRequests.delete(cacheKey);
    });

  articleDetailRequests.set(cacheKey, request);

  return request;
};

export const useBlogPosts = (language: Language): BlogPostsState => {
  const [state, setState] = useState<BlogPostsState>({
    isLoading: true,
    posts: []
  });

  useEffect(() => {
    let isActive = true;

    setState(currentState => ({
      ...currentState,
      isLoading: true
    }));

    void loadArticleList(language)
      .then(articles => {
        if (!isActive) {
          return;
        }

        setState({
          isLoading: false,
          posts: articles
            .map(article => localizeBloggerArticle(article, language))
            .sort(
              (firstPost, secondPost) =>
                Date.parse(secondPost.publishedAt) -
                Date.parse(firstPost.publishedAt)
            )
        });
      })
      .catch(() => {
        if (!isActive) {
          return;
        }

        setState({
          isLoading: false,
          posts: []
        });
      });

    return () => {
      isActive = false;
    };
  }, [language]);

  return state;
};

export const useBlogPost = (
  slug: string | undefined,
  language: Language
): BlogPostState => {
  const [state, setState] = useState<BlogPostState>({
    error: null,
    isLoading: Boolean(slug),
    isNotFound: !slug,
    post: null
  });

  useEffect(() => {
    let isActive = true;

    if (!slug) {
      setState({
        error: null,
        isLoading: false,
        isNotFound: true,
        post: null
      });
      return () => {
        isActive = false;
      };
    }

    setState(currentState => ({
      ...currentState,
      error: null,
      isLoading: true,
      isNotFound: false
    }));

    void loadArticleDetail(slug, language)
      .then(article => {
        if (!isActive) {
          return;
        }

        if (article) {
          setState({
            error: null,
            isLoading: false,
            isNotFound: false,
            post: localizeBloggerArticle(article, language)
          });
          return;
        }

        setState({
          error: null,
          isLoading: false,
          isNotFound: true,
          post: null
        });
      })
      .catch(error => {
        if (!isActive) {
          return;
        }

        setState({
          error:
            error instanceof Error
              ? error
              : new Error('Unable to load the Blogger article.'),
          isLoading: false,
          isNotFound: false,
          post: null
        });
      });

    return () => {
      isActive = false;
    };
  }, [language, slug]);

  return state;
};
